/**
 * @fileoverview Status Banner Component
 * 
 * Sprint 27: Vendor App UX Completion
 * 
 * Displays contextual warnings for:
 * - Market inactive
 * - Vendor suspended
 * - Vendor inactive
 */

import { AlertTriangle, XCircle, AlertCircle } from 'lucide-react';
import { VendorProfile } from '@/lib/constants';

interface StatusBannerProps {
  vendor: VendorProfile | null;
}

export default function StatusBanner({ vendor }: StatusBannerProps) {
  if (!vendor) return null;

  // Check for vendor suspended
  if (vendor.status === 'suspended') {
    return (
      <div className="bg-red-50 border-b border-red-200 px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-800">Account Suspended</p>
            <p className="text-sm text-red-700">
              Your account has been suspended. Please contact support for assistance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check for vendor inactive/pending
  if (vendor.status === 'inactive' || vendor.status === 'pending') {
    return (
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-gray-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-gray-800">You are offline</p>
            <p className="text-sm text-gray-600">
              Your store is not receiving orders. Update your status to go online.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check for market inactive
  if (vendor.market_status === false) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">Market is currently closed</p>
            <p className="text-sm text-amber-700">
              Your market ({vendor.market_name || 'Unknown'}) is inactive. You won't receive orders until it reopens.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // No issues
  return null;
}
